import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangleIcon, ArrowLeftIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, FlagIcon, MoreHorizontalIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { motion } from 'framer-motion';
import { addDays, format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useJournal } from '../context/JournalContext';
import { JournalBlock, Operation, ReviewStatus } from '../lib/types';
import { uid } from '../lib/id';
import { Button } from '../components/ui/Button';
import { OperationForm } from '../components/operations/OperationForm';
import { OperationCard } from '../components/operations/OperationCard';
import { JournalBlockEditor } from '../components/journal/JournalBlockEditor';
import { DeleteToast } from '../components/operations/DeleteToast';
import { cn, fmtR } from '../lib/cn';
import { fmtMoneyEs, fmtPctEs } from '../lib/format';
import { useDisplayMode } from '../context/DisplayModeContext';
import { round } from '../lib/calc';
interface JournalPageProps {
  date: string;
  onBack: () => void;
  onChangeDate: (date: string) => void;
}
export function JournalPage({
  date,
  onBack,
  onChangeDate
}: JournalPageProps) {
  const {
    activeOperations,
    addOperation,
    updateOperation,
    restoreOperation,
    deleteDay,
    restoreDay,
    getDayJournal,
    updateDayJournalField,
    setReviewStatus,
    insertJournalBlocks,
    updateJournalBlock,
    deleteJournalBlock,
    reorderJournalBlocks,
    syncStatus,
    syncError,
    retrySave
  } = useJournal();
  const {
    mode,
    currency
  } = useDisplayMode();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Operation | null>(null);
  const [trashedId, setTrashedId] = useState<string | null>(null);
  const [dayTrashed, setDayTrashed] = useState(false);
  const [confirmDeleteDay, setConfirmDeleteDay] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const journal = getDayJournal(date);
  const dayOps = useMemo(() => activeOperations.filter(operation => operation.date === date).sort((a, b) => (a.entryTime || '').localeCompare(b.entryTime || '')), [activeOperations, date]);
  useEffect(() => {
    setEditing(null);
    setFormOpen(false);
    setMenuOpen(false);
    setConfirmDeleteDay(false);
  }, [date]);
  const totalPct = round(dayOps.reduce((sum, operation) => sum + operation.resultPct, 0));
  const totalR = round(dayOps.reduce((sum, operation) => sum + operation.resultR, 0));
  const totalMoney = round(dayOps.reduce((sum, operation) => sum + (operation.resultMoney ?? 0), 0));
  const wins = dayOps.filter(operation => operation.resultPct > 0).length;
  const losses = dayOps.filter(operation => operation.resultPct < 0).length;
  const winRate = wins + losses ? round(wins / (wins + losses) * 100, 1) : 0;
  const defaultTitle = `Journal · ${format(parseISO(date), "d 'de' MMMM, yyyy", {
    locale: es
  })}`;

  // Persistence state comes from the context, which reports the real Supabase result.
  const markDirty = () => {};
  const insertBlocks = (drafts: Array<Pick<JournalBlock, 'type' | 'content' | 'caption' | 'checked' | 'width'>>, afterId?: string | null) => {
    const blocks: JournalBlock[] = drafts.map((draft, index) => ({
      id: uid('block'),
      type: draft.type,
      content: draft.content,
      caption: draft.caption,
      checked: draft.checked,
      width: draft.type === 'image' ? draft.width ?? 90 : undefined,
      order: journal.blocks.length + index
    }));
    insertJournalBlocks(date, blocks, afterId);
  };
  const navigate = (offset: number) => onChangeDate(format(addDays(parseISO(date), offset), 'yyyy-MM-dd'));
  const title = journal.title || defaultTitle;
  return <main className="min-h-full w-full bg-white" data-id="element-706" data-mp="11h4pob1clt5b">
      <header className="sticky top-0 z-20 border-b border-line bg-white/90 backdrop-blur-md" data-id="element-707" data-mp="11gbmtl5ifnsg">
        <div className="mx-auto flex h-14 max-w-[1280px] items-center justify-between px-5 sm:px-8" data-id="element-708" data-mp="15jhsoropncye">
          <div className="flex items-center gap-1" data-id="element-709" data-mp="1lqmlepp7w9nz">
            <button onClick={onBack} className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium text-ink-soft hover:bg-surface hover:text-ink" aria-label="Volver al calendario" data-id="element-710" data-mp="1gzeeq1f9v2ei">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-711" data-mp="171gqhdxex5lw" /> <span className="hidden sm:inline" data-id="element-712" data-mp="172jgzkqtp61a">Calendario</span>
            </button>
            <span className="mx-1 h-4 w-px bg-line" data-id="element-713" data-mp="24nnvgoxmozys" />
            <button onClick={() => navigate(-1)} className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-soft hover:bg-surface" aria-label="Día anterior" data-id="element-714" data-mp="1zpmc02waiyms"><ChevronLeftIcon className="h-4 w-4" data-id="element-715" data-mp="1uoyb8pi9f3fd" /></button>
            <button onClick={() => navigate(1)} className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-soft hover:bg-surface" aria-label="Día siguiente" data-id="element-716" data-mp="1qph4ztv0e295"><ChevronRightIcon className="h-4 w-4" data-id="element-717" data-mp="jcp2kyquv9ex" /></button>
          </div>
          <div className="relative flex items-center gap-2" data-id="element-718" data-mp="f8zleucifu3x">
            <motion.span key={syncStatus} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.2
          }} title={syncError ?? undefined} className={cn('inline-flex items-center gap-1.5 text-[11px] font-medium', syncStatus === 'error' ? 'text-neg' : 'text-ink-soft')} data-id="element-719" data-mp="2qxe1lo7tr0ee">
              {syncStatus === 'error' ? <>
                  <AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-720" data-mp="3emgkt0i3x1b0" />
                  Sin guardar
                  <button onClick={retrySave} className="underline underline-offset-2 hover:no-underline" data-id="element-721" data-mp="3iojxfsgiibxv">
                    Reintentar
                  </button>
                </> : <>
                  <CheckIcon className="h-3.5 w-3.5 text-pos" data-id="element-722" data-mp="1ob2k0y9tmvgk" />
                  {syncStatus === 'saving' ? 'Guardando…' : 'Guardado'}
                </>}
            </motion.span>
            <button onClick={() => setMenuOpen(open => !open)} className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-soft hover:bg-surface" aria-label="Opciones" data-id="element-723" data-mp="2wjhuhw3uny74">
              <MoreHorizontalIcon className="h-4 w-4" data-id="element-724" data-mp="35kpucim0l73b" />
            </button>
            {menuOpen && <div className="absolute right-0 top-10 w-56 rounded-xl border border-line bg-white p-1.5 shadow-pop" data-id="element-725" data-mp="30ld73wr8dy07">
                <button onClick={() => {
              setMenuOpen(false);
              setConfirmDeleteDay(true);
            }} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] text-ink-soft hover:bg-neg-soft hover:text-neg" data-id="element-726" data-mp="3fi3wdd76xtc3">
                  <Trash2Icon className="h-3.5 w-3.5" data-id="element-727" data-mp="3ka3zfeik52cw" />Eliminar día
                </button>
                <p className="px-2 py-1.5 text-[11px] leading-relaxed text-ink-soft" data-id="element-728" data-mp="23zhbjs0lts4">
                  Gestiona las etiquetas y categorías desde la pestaña Etiquetas.
                </p>
              </div>}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1120px] px-5 py-7 sm:px-8 lg:px-14 lg:py-9" data-id="element-729" data-mp="grpp5ibwefcj">
        <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-ink-soft capitalize" data-id="element-730" data-mp="lpiw4dl0zddv">
          {format(parseISO(date), 'EEEE · d MMMM yyyy', {
          locale: es
        })}
        </p>
        <input aria-label="Título del journal" value={title} onFocus={() => {
        if (!journal.title) {
          updateDayJournalField(date, 'title', defaultTitle);
          markDirty();
        }
      }} onChange={event => {
        updateDayJournalField(date, 'title', event.target.value);
        markDirty();
      }} className="mt-2 w-full bg-transparent text-3xl font-semibold tracking-tight placeholder:text-ink-soft/40 focus:outline-none sm:text-4xl" data-id="element-731" data-mp="bqtl94rjqt74" />

        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-7" data-id="element-732" data-mp="3b7rxwe6b36o1">
          <Metric label={mode === 'money' ? 'Resultado dinero' : 'Resultado %'} value={dayOps.length ? mode === 'money' ? fmtMoneyEs(totalMoney, true, currency) : fmtPctEs(totalPct) : '—'} tone={totalPct > 0 ? 'pos' : totalPct < 0 ? 'neg' : 'neutral'} data-id="element-733" data-mp="3f8249ry0fz3f" />
          <Metric label={mode === 'money' ? 'Resultado %' : 'Resultado dinero'} value={dayOps.length ? mode === 'money' ? fmtPctEs(totalPct) : fmtMoneyEs(totalMoney, true, currency) : '—'} tone={totalMoney > 0 ? 'pos' : totalMoney < 0 ? 'neg' : 'neutral'} data-id="element-734" data-mp="3loby9m4343qo" />
          <Metric label="Resultado R" value={dayOps.length ? fmtR(totalR) : '—'} tone={totalR > 0 ? 'pos' : totalR < 0 ? 'neg' : 'neutral'} data-id="element-735" data-mp="37x77l9ip2lsh" />
          <Metric label="Win Rate" value={dayOps.length ? `${winRate.toFixed(1)}%` : '—'} data-id="element-736" data-mp="2y0kmqkqdbuqg" />
          <Metric label="Operaciones" value={String(dayOps.length)} data-id="element-737" data-mp="2t17zvzzhynnv" />

          <div className="rounded-xl border border-line bg-surface/35 px-3 py-2.5" data-id="element-738" data-mp="2t0g8l1cxnc3e">
            <p className="text-[10px] font-medium text-ink-soft" data-id="element-739" data-mp="2p05z3epxm3js">¿Se siguió el plan?</p>
            <div className="mt-1.5 flex gap-1" data-id="element-740" data-mp="14gtssja5c0k0">
              {([{
              label: 'Sí',
              value: true
            }, {
              label: 'No',
              value: false
            }] as const).map(option => <button key={option.label} onClick={() => {
              updateDayJournalField(date, 'followedPlan', option.value);
              markDirty();
            }} className={cn('rounded-md px-2 py-1 text-[11px] font-medium', journal.followedPlan === option.value ? option.value ? 'bg-pos text-white' : 'bg-neg text-white' : 'bg-white text-ink-soft hover:text-ink')} data-id="element-741" data-mp="1dijk7p412zhs">
                  {option.label}
                </button>)}
            </div>
          </div>

          <ReviewCard status={journal.reviewStatus ?? 'none'} onChange={status => {
          setReviewStatus(date, status);
          markDirty();
        }} data-id="element-742" data-mp="23wmc64mknhp" />
        </div>

        <section className="mt-7 border-t border-line pt-6" data-id="element-743" data-mp="1di4ugt6cl7jy">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-744" data-mp="1dhmo33pz1cyb">
            <div data-id="element-745" data-mp="1ifocm46624h6">
              <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-746" data-mp="1if4tik4s5a1k">Operaciones</h2>
              <p className="mt-1 text-[12px] text-ink-soft" data-id="element-747" data-mp="1nglpc18eq6ba">Registra cada operación con su Dirección, Zona y Entrada.</p>
            </div>
            <Button variant="primary" size="sm" onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }} data-id="element-748" data-mp="ureupdc2q0vb">
              <PlusIcon className="h-4 w-4" data-id="element-749" data-mp="zq8712fmjltw" />Nueva operación
            </Button>
          </div>
          <div className="mt-4 space-y-2" data-id="element-750" data-mp="14pkqreb78cs9">
            {dayOps.length ? dayOps.map(operation => <OperationCard key={operation.id} op={operation} onEdit={op => {
            setEditing(op);
            setFormOpen(true);
          }} onDeleted={id => setTrashedId(id)} data-id="element-751" data-mp="19gjc4sb1o12d" />) : <div className="rounded-xl border border-dashed border-line bg-surface/30 px-5 py-8 text-center" data-id="element-752" data-mp="2ua014g5dhrdu">
                <p className="text-sm font-medium" data-id="element-753" data-mp="2q852xkiqnu4a">Aún no hay operaciones</p>
                <p className="mt-1 text-[12px] text-ink-soft" data-id="element-754" data-mp="2l9lbj4ldlq4j">Añade una operación y escribe tu análisis en Notas.</p>
              </div>}
          </div>
        </section>

        <JournalBlockEditor blocks={[...journal.blocks].sort((a, b) => a.order - b.order)} onInsert={(drafts, afterId) => {
        insertBlocks(drafts, afterId);
        markDirty();
      }} onUpdate={(blockId, patch) => {
        updateJournalBlock(date, blockId, patch);
        markDirty();
      }} onDelete={blockId => deleteJournalBlock(date, blockId)} onReorder={reorderJournalBlocks.bind(null, date)} onDirty={markDirty} data-id="element-755" data-mp="2gcl68vwcoreh" />
      </div>

      <DeleteToast open={!!trashedId} onUndo={() => {
      if (trashedId) restoreOperation(trashedId);
      setTrashedId(null);
    }} onDismiss={() => setTrashedId(null)} data-id="element-756" data-mp="1sjl7t92m2j39" />

      <DeleteToast open={dayTrashed} message="El día se ha movido a la papelera." onUndo={() => {
      restoreDay(date);
      setDayTrashed(false);
    }} onDismiss={() => setDayTrashed(false)} data-id="element-757" data-mp="1dx6275wiyx7u" />

      {confirmDeleteDay && <div className="fixed inset-0 z-[85] flex items-center justify-center bg-ink/30 p-5" role="dialog" aria-modal="true" data-id="element-758" data-mp="18z4gsebmmlt7">
          <div className="w-full max-w-md rounded-2xl border border-line bg-white p-5 shadow-pop" data-id="element-759" data-mp="1wo6ytna6ago8">
            <h2 className="text-[15px] font-semibold tracking-tight" data-id="element-760" data-mp="21obgvhqqbjgk">¿Quieres mover este día completo a la papelera?</h2>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-soft" data-id="element-761" data-mp="1nvlqz282xue6">
              Se moverán el Journal, sus notas, imágenes y todas las operaciones asociadas. Podrás restaurarlo durante 30 días.
            </p>
            <div className="mt-5 flex justify-end gap-2" data-id="element-762" data-mp="2fm9qk9u3urpq">
              <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteDay(false)} data-id="element-763" data-mp="2kejczfw0h9n9">Cancelar</Button>
              <Button variant="danger" size="sm" onClick={() => {
            deleteDay(date);
            setConfirmDeleteDay(false);
            setDayTrashed(true);
          }} data-id="element-764" data-mp="8y92fojzykrn">
                Mover a la papelera
              </Button>
            </div>
          </div>
        </div>}

      {formOpen && <OperationForm date={date} initial={editing} onCancel={() => setFormOpen(false)} onSubmit={operation => {
      editing ? updateOperation(operation) : addOperation(operation);
      setFormOpen(false);
      markDirty();
    }} data-id="element-765" data-mp="dqsqjfgb8yap" />}
    </main>;
}

/** Review state is independent from plan compliance. */
function ReviewCard({
  status,
  onChange
}: {
  status: ReviewStatus;
  onChange: (status: ReviewStatus) => void;
}) {
  const pending = status === 'pending';
  const reviewed = status === 'reviewed';
  const style = pending ? {
    background: '#fff8dc',
    borderColor: '#e8c75a',
    color: '#8a6a00'
  } : reviewed ? {
    background: '#f1f2f4',
    borderColor: '#dfe2e6',
    color: '#626873'
  } : {
    background: '#ffffff',
    borderColor: '#e5e7eb',
    color: '#6b7280'
  };
  return <div className="rounded-xl border px-3 py-2.5" style={{
    ...style,
    borderWidth: 1,
    borderStyle: 'solid'
  }} data-id="element-766" data-mp="3l3dp0pshd7nu">
      <p className="flex items-center gap-1.5 text-[10px] font-medium" data-id="element-767" data-mp="3q3sz98hgtz1l">
        <FlagIcon className="h-3 w-3" data-id="element-768" data-mp="3v22zvylylrul" />Revisión
      </p>
      <p className="mt-1 text-[12px] font-semibold" data-id="element-769" data-mp="3v1ktwa9hvz9h">
        {pending ? 'Pendiente' : reviewed ? 'Revisado' : 'Sin marcar'}
      </p>
      <div className="mt-1.5 flex gap-1" data-id="element-770" data-mp="8mi6jmcul614">
        <button onClick={() => onChange(pending ? 'none' : 'pending')} className={cn('rounded-md border border-current/25 bg-white/70 px-1.5 py-0.5 text-[10px] font-medium', pending && 'bg-white')} data-id="element-771" data-mp="8njgtom1hohs">
          Pendiente
        </button>
        <button onClick={() => onChange(reviewed ? 'none' : 'reviewed')} className={cn('rounded-md border border-current/25 bg-white/70 px-1.5 py-0.5 text-[10px] font-medium', reviewed && 'bg-white')} data-id="element-772" data-mp="mlhv6u0d1kxa">
          Revisado
        </button>
      </div>
    </div>;
}
function Metric({
  label,
  value,
  tone = 'neutral'
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'pos' | 'neg';
}) {
  return <div className="rounded-xl border border-line bg-surface/35 px-3 py-2.5" data-id="element-773" data-mp="ir8afxrtak7z">
      <p className="text-[10px] font-medium text-ink-soft" data-id="element-774" data-mp="1fgz9mab1svk4">{label}</p>
      <p className={cn('mt-1 truncate text-sm font-semibold tabular-nums tracking-tight', tone === 'pos' && 'text-pos', tone === 'neg' && 'text-neg')} data-id="element-775" data-mp="1ahctnt3u517m">
        {value}
      </p>
    </div>;
}